import {Box, createStyles, Stack} from '@mantine/core';
import React from 'react';

import {ProcedureRunTab} from 'studies/redux/ProcedureRunPlanUIReduxActions';
import getTestProps from 'test/lib/cypress/getTestProps';
import {Tab, TabContainer} from 'ui-library/TabContainer';

import {type StudyExecutionHeaderTabsProps} from './StudyExecutionHeader';

interface ProcedureRunLayoutProps {
  header: React.ReactNode;
  tabProps: StudyExecutionHeaderTabsProps<ProcedureRunTab>;
  execution: React.ReactNode;
}

export default function ProcedureRunLayout({
  header,
  tabProps,
  execution,
}: ProcedureRunLayoutProps): JSX.Element {
  const {classes, cx} = useStyles();
  // The overview tab scrolls with the page. Design and plan tabs pin the header and scroll their own
  // inner region.
  const isOverviewTab = tabProps.headerTabContainerProps?.activeTab === ProcedureRunTab.OVERVIEW;
  return (
    <Stack
      spacing={0}
      className={isOverviewTab ? classes.containerScroll : classes.container}
      {...getTestProps({component: 'ProcedureRunLayout', element: 'container'})}
    >
      <Box className={classes.header}>{header}</Box>
      {tabProps.headerTabContainerProps ? (
        <Box
          className={isOverviewTab ? classes.bodyScroll : classes.body}
          {...getTestProps({component: 'ProcedureRunLayout', element: 'body'})}
        >
          <TabContainer {...tabProps.headerTabContainerProps}>
            {tabProps.headerTabsProps.map((headerTabProps) => (
              <Tab key={headerTabProps.tabKey} {...headerTabProps}>
                {isOverviewTab ? (
                  execution
                ) : (
                  <Box
                    className={classes.scrollableContent}
                    {...getTestProps({component: 'ProcedureRunLayout', element: 'scrollRegion'})}
                  >
                    {execution}
                  </Box>
                )}
              </Tab>
            ))}
          </TabContainer>
        </Box>
      ) : (
        <Box
          className={cx(classes.scrollableContent, classes.execution)}
          {...getTestProps({component: 'ProcedureRunLayout', element: 'scrollRegion'})}
        >
          {execution}
        </Box>
      )}
    </Stack>
  );
}

const useStyles = createStyles((theme) => ({
  // Pinned layout (design/plan): trap overflow so the header stays put and inner regions scroll.
  container: {
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
    width: '100%',
  },
  // Page-scroll layout (run overview): let overflow bubble up to the surrounding scroll container.
  containerScroll: {
    height: '100%',
    width: '100%',
  },
  header: {
    flexShrink: 0,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  bodyScroll: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  // Single scroll region for pinned tab and plan content, so the header stays put while long content
  // scrolls. Design tabs fill it exactly and scroll their own side panels.
  scrollableContent: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
  },
  execution: {
    backgroundColor: theme.colors.gray[0],
  },
}));
